// src/components/FeaturesDept.jsx
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal from "./Reveal";
import { FEATURES_DEPT } from "../data/featuresDept";


const IconCar = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-7 w-7 text-white/95"
    aria-hidden="true"
  >
    <path
      fill="currentColor"
      d="M5 11l1.5-4.5A2 2 0 018.4 5h7.2a2 2 0 011.9 1.5L19 11h1a1 1 0 011 1v4h-1a2 2 0 11-4 0H9a2 2 0 11-4 0H4v-4a1 1 0 011-1h0zm2.2-4l-1 3h11.6l-1-3a1 1 0 00-.96-.7H8.16a1 1 0 00-.96.7z"
    />
  </svg>
);

const IconCode = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-7 w-7 text-white/95"
    aria-hidden="true"
  >
    <path
      fill="currentColor"
      d="M9.4 16.6L5.8 13l3.6-3.6L8 8l-5 5 5 5 1.4-1.4zm5.2 0L18.8 13l-4.2-4.2L16 8l5 5-5 5-1.4-1.4zM14.1 4.1L11 20l-1.9-.4 3.1-15.9 1.9.4z"
    />
  </svg>
);


function DeptArt({ variant = "zentra", className = "" }) {
  const maps = {
   
    blue:
      "bg-[radial-gradient(520px_220px_at_10%_0%,rgba(54,192,185,0.55),transparent_60%),radial-gradient(520px_260px_at_90%_120%,rgba(32,75,151,0.85),transparent_65%)]",


    black:
      "bg-[radial-gradient(520px_220px_at_15%_0%,rgba(124,58,237,0.55),transparent_60%),radial-gradient(520px_260px_at_90%_120%,rgba(15,23,42,0.95),transparent_65%)]",

    
    orange:
      "bg-[radial-gradient(520px_200px_at_80%_-10%,rgba(249,115,22,.45),transparent_60%),radial-gradient(420px_200px_at_20%_110%,rgba(234,88,12,.35),transparent_60%)]",
    purple:
      "bg-[radial-gradient(520px_220px_at_20%_0%,rgba(168,85,247,.45),transparent_60%),radial-gradient(500px_220px_at_90%_120%,rgba(59,130,246,.35),transparent_60%)]",
    pink:
      "bg-[radial-gradient(520px_200px_at_70%_0%,rgba(244,114,182,.45),transparent_60%),radial-gradient(500px_220px_at_5%_120%,rgba(251,113,133,.35),transparent_60%)]",

  
    zentra:
      "bg-[radial-gradient(520px_220px_at_50%_-10%,rgba(32,75,151,.70),transparent_60%),radial-gradient(500px_240px_at_90%_120%,rgba(64,196,198,.40),transparent_60%)]",
  };

  const bg = maps[variant] ?? maps.zentra;

  const isGPS = variant === "blue";
  const isSistemas = variant === "black";

  return (
    <div
      className={`relative h-32 rounded-2xl overflow-hidden ${bg} ${className}`}
    >
     
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/0 to-black/25" />
      
      <div className="absolute right-10 top-6 h-16 w-16 rounded-full bg-white/14 backdrop-blur-sm" />
      <div className="absolute right-3 top-10 h-20 w-20 rounded-full bg-black/25" />

     
      {(isGPS || isSistemas) && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="inline-flex items-center gap-2">
            {/* Pill solo icono */}
            <div className="inline-flex items-center justify-center rounded-2xl bg-black/30 px-4 py-3 border border-white/20 shadow-[0_18px_45px_rgba(15,23,42,0.45)] backdrop-blur-sm">
              {isGPS && <IconCar />}
              {isSistemas && <IconCode />}
            </div>

            
            <div className="inline-flex items-center justify-center rounded-2xl bg-black/22 px-4 py-2 border border-white/18 backdrop-blur-sm">
              <span className="text-[11px] uppercase tracking-[0.16em] text-white/90">
                {isGPS ? "GPS" : "Sistemas"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" className="text-white">
    <path fill="currentColor" d="M11 11V6h2v5h5v2h-5v5h-2v-5H6v-2z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" className="text-slate-900">
    <path
      fill="currentColor"
      d="M18.3 5.71L12 12.01l-6.3-6.3-1.41 1.41 6.3 6.3-6.3 6.3 1.41 1.41 6.3-6.3 6.3 6.3 1.41-1.41-6.3-6.3 6.3-6.3z"
    />
  </svg>
);


function DeptModal({ open, onClose, feature }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && feature && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-slate-950/60 backdrop-blur-sm flex items-start md:items-center justify-center overflow-y-auto px-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 140, damping: 18 }}
            className="relative my-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl border border-slate-200/80 bg-white shadow-[0_40px_120px_rgba(15,23,42,0.45)]"
            onClick={(e) => e.stopPropagation()}
          >
          
            <div className="relative p-4 pb-0">
              <DeptArt variant={feature.art} className="h-40 md:h-48" />
              <button
                onClick={onClose}
                aria-label="Cerrar"
                className="absolute right-6 top-6 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow-sm hover:bg-white"
              >
                <CloseIcon />
              </button>
            </div>

           
            <div className="px-6 pt-6 pb-8 md:px-8 md:pb-10 text-slate-900">
              <h3 className="text-2xl md:text-3xl font-bold tracking-tight">
                {feature.title}
              </h3>
              <p className="mt-2 text-slate-600">{feature.blurb}</p>

              <ul className="mt-6 space-y-4 text-sm md:text-base">
                {feature.bullets.map((b, idx) => (
                  <li key={idx} className="flex gap-3">
                    <span className="mt-2 block h-1.5 w-1.5 rounded-full bg-[var(--brand-2)]" />
                    <p>
                      <strong className="text-slate-900">{b.strong}</strong>{" "}
                      <span className="text-slate-700">{b.text}</span>
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ------------------------------ Tarjeta ------------------------------ */
function DeptCard({ item, onOpen }) {
  return (
    <div
      className="group relative flex flex-col rounded-[26px] border border-slate-200/70 
                 bg-white/80 backdrop-blur-sm p-4 shadow-[0_22px_60px_rgba(15,23,42,0.09)]
                 hover:shadow-[0_30px_80px_rgba(15,23,42,0.16)] transition-shadow duration-300"
    >
      <DeptArt variant={item.art} />
      <div className="p-5">
        <h3 className="text-xl font-semibold text-slate-900">
          {item.title}
        </h3>
        <p className="mt-2 text-sm text-slate-600">{item.blurb}</p>

        
        <button
          onClick={() => onOpen(item)}
          aria-label="Más detalles"
          className="absolute bottom-5 right-5 inline-flex h-10 w-10 items-center justify-center
                     rounded-2xl text-white shadow-[0_10px_30px_rgba(15,23,42,0.35)]
                     hover:brightness-110 transition-all duration-200"
          style={{
            background:
              "linear-gradient(135deg, var(--brand-1) 0%, var(--brand-3) 40%, var(--brand-4) 100%)",
          }}
        >
          <PlusIcon />
        </button>
      </div>
    </div>
  );
}

/* --------------------------- Sección completa --------------------------- */
export default function FeaturesDept() {
  const [active, setActive] = useState(null);

  return (
    <section id="servicios" className="relative bg-white">
      
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[360px]
                   bg-[radial-gradient(1000px_260px_at_50%_100%,rgba(148,163,184,0.25),transparent_70%)]"
      />

      <div className="relative z-10 mx-auto max-w-6xl px-4 py-16">
        
        <Reveal>
          <div
            className="rounded-[26px] border border-slate-200/70 bg-white/80 backdrop-blur-sm 
                       shadow-[0_22px_60px_rgba(15,23,42,0.08)] px-6 py-6 md:px-8 md:py-7"
          >
            <h2
              className="
                heading-display text-4xl md:text-5xl font-bold
                text-transparent bg-clip-text
                bg-gradient-to-r from-[var(--brand-1)] via-[var(--brand-3)] to-[var(--brand-4)]
              "
            >
              Nuestros servicios
            </h2>
            <p className="mt-3 max-w-3xl text-slate-600">
              Dos unidades diseñadas para ti: desarrollo de sistemas a medida y
              gestión inteligente de flotas en tiempo real.
            </p>
          </div>
        </Reveal>

        
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {FEATURES_DEPT.map((f, i) => (
            <Reveal key={f.id} delay={i * 0.05}>
              <DeptCard item={f} onOpen={setActive} />
            </Reveal>
          ))}
        </div>
      </div>

      <DeptModal
        open={!!active}
        feature={active}
        onClose={() => setActive(null)}
      />
    </section>
  );
}
